/**
 * Calendar event types for the unified calendar system.
 * Maps directly to the backend calendar_events table fields.
 */

export interface CalendarEvent {
  id: number;
  uuid: string;
  title: string;
  description?: string;
  location?: string;
  color?: string;
  start_time: string;         // ISO datetime
  end_time?: string;          // ISO datetime
  all_day: boolean;
  event_type: CalendarEventType;
  source_type?: CalendarSourceType;
  source_id?: number;
  source_uuid?: string;
  recurrence?: string;        // 'daily' | 'weekly' | 'monthly' | 'yearly' | null
  reminder_minutes?: number;
  ical_uid?: string;
  status: CalendarEventStatus;
  visibility: 'private' | 'public' | 'organization';
  shared_with?: CalendarCollaborator[];
  collaborators?: CalendarCollaborator[];
  tags?: string[];
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: number;
}

export type CalendarEventType =
  | 'task' | 'task_deadline'
  | 'matter' | 'matter_deadline'
  | 'project' | 'project_deadline'
  | 'hearing'
  | 'meeting'
  | 'consultation'
  | 'filing_deadline'
  | 'statute_of_limitations'
  | 'client_meeting'
  | 'court_date'
  | 'reminder'
  | 'custom';

export type CalendarSourceType = 'task' | 'matter' | 'project' | 'client' | 'manual';

export type CalendarEventStatus = 'active' | 'completed' | 'cancelled' | 'tentative';

export interface CalendarCollaborator {
  user_id?: number;
  uuid?: string;
  name?: string;
  email?: string;
  initials?: string;
  profile_picture?: string | null;
  access?: string;
  // Legacy uppercase keys
  Name?: string;
  Email?: string;
}

export interface CalendarFilters {
  event_type?: CalendarEventType;
  source_type?: CalendarSourceType;
  status?: CalendarEventStatus;
  search?: string;
}

export interface CalendarState {
  events: CalendarEvent[];
  selectedEvent: CalendarEvent | null;
  loading: boolean;
  error: string | null;
  filters: CalendarFilters;
  feedToken: string | null;
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
}

// Redux action type interfaces
export type CalendarActionTypes =
  | { type: 'FETCH_CALENDAR_EVENTS'; payload: CalendarEvent[] }
  | { type: 'FETCH_CALENDAR_EVENTS_LOADING' }
  | { type: 'FETCH_CALENDAR_EVENTS_ERROR'; payload: string }
  | { type: 'SET_CALENDAR_EVENT'; payload: CalendarEvent }
  | { type: 'CREATE_CALENDAR_EVENT'; payload: CalendarEvent }
  | { type: 'UPDATE_CALENDAR_EVENT'; payload: CalendarEvent }
  | { type: 'DELETE_CALENDAR_EVENT'; payload: string }    // uuid
  | { type: 'SET_CALENDAR_FILTERS'; payload: Partial<CalendarFilters> }
  | { type: 'SET_CALENDAR_FEED_TOKEN'; payload: string }
  | { type: 'SET_CALENDAR_SYNC_STATUS'; payload: CalendarState['syncStatus'] }
  | { type: 'CLEAR_CALENDAR_EVENTS' };

/** Color config per event_type for visual distinction */
export const EVENT_TYPE_COLORS: Record<CalendarEventType, { bg: string; text: string; dot: string; darkBg: string; darkText: string }> = {
  task:                    { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500',    darkBg: 'dark:bg-blue-900/30',   darkText: 'dark:text-blue-300' },
  task_deadline:           { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500',    darkBg: 'dark:bg-blue-900/30',   darkText: 'dark:text-blue-300' },
  matter:                  { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500',  darkBg: 'dark:bg-purple-900/30', darkText: 'dark:text-purple-300' },
  matter_deadline:         { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500',  darkBg: 'dark:bg-purple-900/30', darkText: 'dark:text-purple-300' },
  project:                 { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  project_deadline:        { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  hearing:                 { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     darkBg: 'dark:bg-red-900/30',    darkText: 'dark:text-red-300' },
  meeting:                 { bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500',   darkBg: 'dark:bg-green-900/30',  darkText: 'dark:text-green-300' },
  consultation:            { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500',    darkBg: 'dark:bg-teal-900/30',   darkText: 'dark:text-teal-300' },
  filing_deadline:         { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-500',  darkBg: 'dark:bg-orange-900/30', darkText: 'dark:text-orange-300' },
  statute_of_limitations:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500',    darkBg: 'dark:bg-rose-900/30',   darkText: 'dark:text-rose-300' },
  client_meeting:          { bg: 'bg-cyan-50',    text: 'text-cyan-700',    dot: 'bg-cyan-500',    darkBg: 'dark:bg-cyan-900/30',   darkText: 'dark:text-cyan-300' },
  court_date:              { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   darkBg: 'dark:bg-amber-900/30',  darkText: 'dark:text-amber-300' },
  reminder:                { bg: 'bg-yellow-50',  text: 'text-yellow-700',  dot: 'bg-yellow-500',  darkBg: 'dark:bg-yellow-900/30', darkText: 'dark:text-yellow-300' },
  custom:                  { bg: 'bg-gray-50',    text: 'text-gray-700',    dot: 'bg-gray-500',    darkBg: 'dark:bg-gray-900/30',   darkText: 'dark:text-gray-300' },
};
